// LoginContext.js
import React, { createContext, useEffect, useState } from 'react';
import { apiFetch as fetch } from "apiClient";

export const LoginContext = createContext(null);

export let globalKiteStatus = {
    readyForTrading: false,
};
export function updateGlobalKiteStatus(status) {
    globalKiteStatus.readyForTrading    = !!status?.readyForTrading;
}

export const LoginProvider = ({ children }) => {
    const [zerodhaConnectionStatusDTO, setZerodhaConnectionStatusDTO] = useState(null);
    const [isLoginVerified, setIsLoginVerified] = useState(false);

    //
    // 1️⃣ Zerodha login navigation (your original handleLoginFunction)
    //
    const handleKiteLogin = () => {
        window.location.href = `${process.env.REACT_APP_API_BASE}/api/kite/login`;
    };
    const handleKiteLoginForUser = (zerodhaUserId) => {
        const id = (zerodhaUserId || "").toUpperCase();
        let path = "/api/kite/login";

        if (id === "SJ0168") {
            path = "/api/kite/login/sj";
        } else if (id === "DA7237") {
            path = "/api/kite/login/da";
        }
        window.location.href = `${process.env.REACT_APP_API_BASE}${path}`;
    };

    //
    // 2️⃣ Redirect to your own APP login page
    //
    const redirectToAppLogin = () => {
        const currentPath = window.location.pathname;
        if (currentPath !== "/login") {
            window.location.href = "/login";
        }
    };

    //
    // 3️⃣ Set banner based on Zerodha portion of status
    //
    const applyBannerFromStatus = (s) => {
        if (!window.showGlobalBanner || !window.clearGlobalBanner) {
            return;
        }

        // 🔹 New: if backend is still initializing, show that and bail out
        if (s.backendInitializing) {
            window.showGlobalBanner("Backend initializing…");
            return;
        }

        // 🔹 If some required Zerodha users are NOT logged in → show them
        if (Array.isArray(s.missingRequiredZerodhaUserIds) &&
            s.missingRequiredZerodhaUserIds.length > 0) {
            window.showGlobalBanner(
                "Login to Zerodha for: " + s.missingRequiredZerodhaUserIds.join(", ")
            );
            return;
        }

        // App is already confirmed logged-in at this point.
        if (!s.readyForTrading) {
            window.showGlobalBanner("Zerodha not ready");
        } else {
            window.clearGlobalBanner();
        }
    };


    //
    // 4️⃣ Core: fetch session status from backend
    //
    const refreshStatus = async () => {
        try {
            const res  = await fetch(`/api/kitesession/status`);
            const data = await res.json();

            if (!data || !data.appLoggedIn) {
                redirectToAppLogin();
                return;
            }

            setZerodhaConnectionStatusDTO(data);
            updateGlobalKiteStatus(data);

            // 🔥 NEW LOGIC:
            // Do NOT verify login until backendInitializing == false
            if (data.backendInitializing) {
                // Show message (handled in the render return block)
                // AND re-check after a pause
                setTimeout(refreshStatus, 2500);
                return;
            }

            // 🔥 Only after backend initialization is complete:
            setIsLoginVerified(true);
            applyBannerFromStatus(data);

        } catch (err) {
            console.error("Error checking session status:", err);
            redirectToAppLogin();
        }
    };

    //
    // 5️⃣ Run once on mount
    //
    useEffect(() => {
        refreshStatus();

        const evt = new EventSource(`${process.env.REACT_APP_API_BASE}/events/stream`);

        evt.addEventListener("SessionStatus", (e) => {
            try {
                const data = JSON.parse(e.data);

                setZerodhaConnectionStatusDTO(data);
                updateGlobalKiteStatus(data);
                applyBannerFromStatus(data);
            } catch (err) {
                console.error("Failed SSE SessionStatus parse:", err);
            }
        });

        evt.onerror = (err) => console.warn("SessionStatus SSE error:", err);

        return () => evt.close();
    }, []);

    //
    // 6️⃣ Block UI only until we know whether app is logged in
    //

    if (!isLoginVerified) {
        return (
            <div className="at-loading-screen">
                <div className="at-loading-card">
                    <div className="at-spinner"></div>

                    <div className="at-loading-title">
                        {zerodhaConnectionStatusDTO?.backendInitializing
                            ? "Backend Initializing…"
                            : "Verifying login…"}
                    </div>
                </div>
            </div>
        );
    }


    //
    // 7️⃣ Provide context values
    //
    return (
        <LoginContext.Provider
            value={{
                // New preferred name
                handleKiteLogin,
                handleKiteLoginForUser,
                // Backwards compatibility: existing code can still use handleLoginFunction
                handleLoginFunction: handleKiteLogin,
                zerodhaConnectionStatusDTO,
                refreshStatus
            }}
        >
            {children}
        </LoginContext.Provider>
    );
};
